import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  linkWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  EmailAuthProvider,
  type OAuthCredential,
} from "firebase/auth";
import { AuthShell } from "../components/auth-shell";
import { auth } from "../firebase";
import { clearPendingLink, readPendingLink, type PendingLink } from "../lib/account-linking";

/**
 * Provider-conflict merger. Reads the pending link from sessionStorage (set
 * by auth-form when it detected `account-exists-with-different-credential`
 * or `email-already-in-use` against a Google-only account) and walks the
 * user through re-authenticating with their original provider so we can
 * link the new credential to the existing account.
 *
 * Two modes:
 *  - "add-google": account is password, user clicked Google → we ask the
 *    password to sign in, then link the stashed Google credential.
 *  - "add-password": account is Google, user filled signup form → we ask
 *    them to sign in with Google, then link the stashed password as a
 *    second credential.
 */
export function LinkAccountPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [pending, setPending] = useState<PendingLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    const data = readPendingLink();
    if (!data) {
      nav("/login", { replace: true });
      return;
    }
    setPending(data);
  }, [nav]);

  async function onLinkSuccess() {
    clearPendingLink();
    nav("/home", { replace: true });
  }

  async function submitAddGoogle(e: FormEvent) {
    e.preventDefault();
    if (!pending || pending.mode !== "add-google") return;
    setBusy(true);
    setErrorKey(null);
    try {
      // Restore the Google credential stashed at conflict time.
      const credJson = JSON.parse(pending.googleCredJson);
      const restored: OAuthCredential | null = OAuthProvider.credentialFromJSON
        ? OAuthProvider.credentialFromJSON(credJson)
        : null;
      if (!restored) {
        setErrorKey("linkAccount.errors.restoreFailed");
        return;
      }
      await signInWithEmailAndPassword(auth, pending.email, passwordInput);
      if (!auth.currentUser) {
        setErrorKey("linkAccount.errors.linkFailed");
        return;
      }
      await linkWithCredential(auth.currentUser, restored);
      await onLinkSuccess();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        setErrorKey("auth.errors.wrongCredentials");
      } else if (code === "auth/too-many-requests") {
        setErrorKey("auth.errors.tooManyAttempts");
      } else {
        console.error("[link-account] add-google link failed", err);
        setErrorKey("linkAccount.errors.linkFailed");
      }
    } finally {
      setBusy(false);
    }
  }

  async function startAddPassword() {
    if (!pending || pending.mode !== "add-password") return;
    setBusy(true);
    setErrorKey(null);
    try {
      const provider = new GoogleAuthProvider();
      // Hint Google to use the conflicted email so the user doesn't pick the
      // wrong account in the chooser.
      provider.setCustomParameters({ login_hint: pending.email });
      await signInWithPopup(auth, provider);
      if (!auth.currentUser) {
        setErrorKey("linkAccount.errors.linkFailed");
        return;
      }
      // Sanity: the Google account they picked must match the original email.
      if (auth.currentUser.email !== pending.email) {
        setErrorKey("linkAccount.errors.emailMismatch");
        await auth.signOut();
        return;
      }
      const passwordCred = EmailAuthProvider.credential(pending.email, pending.pendingPassword);
      await linkWithCredential(auth.currentUser, passwordCred);
      await onLinkSuccess();
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      if (code === "auth/popup-closed-by-user") {
        setErrorKey("auth.errors.popupClosed");
      } else {
        console.error("[link-account] add-password link failed", err);
        setErrorKey("linkAccount.errors.linkFailed");
      }
    } finally {
      setBusy(false);
    }
  }

  if (!pending) return null;

  if (pending.mode === "add-google") {
    return (
      <AuthShell
        formTitle={t("linkAccount.addGoogle.title")}
        formSub={t("linkAccount.addGoogle.sub", { email: pending.email })}
      >
        <form onSubmit={submitAddGoogle} className="acx__form" noValidate>
          <div className="acx__field">
            <label htmlFor="link-pw" className="acx__label">{t("auth.password")}</label>
            <input
              id="link-pw"
              className="acx__input"
              type="password"
              required
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              placeholder={t("auth.passwordPlaceholderLogin")}
              autoComplete="current-password"
              disabled={busy}
            />
          </div>
          <button type="submit" className="acx__btn acx__btn--primary acx__btn--full" disabled={busy}>
            {busy ? t("auth.working") : t("linkAccount.addGoogle.cta")}
          </button>
          {errorKey && (
            <div className="acx__alert acx__alert--danger" role="alert">
              {t(errorKey)}
            </div>
          )}
        </form>
      </AuthShell>
    );
  }

  // pending.mode === "add-password"
  return (
    <AuthShell
      formTitle={t("linkAccount.addPassword.title")}
      formSub={t("linkAccount.addPassword.sub", { email: pending.email })}
    >
      <div className="acx__stack acx__stack--lg">
        <button
          type="button"
          className="acx__btn acx__btn--primary acx__btn--full"
          onClick={startAddPassword}
          disabled={busy}
        >
          {busy ? t("auth.working") : t("linkAccount.addPassword.cta")}
        </button>
        {errorKey && (
          <div className="acx__alert acx__alert--danger" role="alert">
            {t(errorKey)}
          </div>
        )}
      </div>
    </AuthShell>
  );
}
